'use client';

import ProductBanner from '@components/ProductBanner';
import React from 'react';
import Image from '@repo/ui/src/components/Image';
import ProductPrice from '@repo/ui/src/components/Product/Price';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { classNames } from '@repo/ui/src/utils/classNames';
import { logProduct } from '@utils/productWrapper';
import { Anchor } from '@repo/ui/src/components/Anchor';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import RatingStars from '@repo/ui/src/components/RatingStars';
import { useVipAccess } from '@hooks/customer';
import { normalize } from './ProductCard.helpers';
import { ProductProps } from '@repo/ui/src/types';

interface Props {
  product: ProductProps;
  _testID?: string;
  index?: number;
  listName?: string;
  newText?: string;
  saleText?: string;
  hideAddToCartButton?: boolean;
  hideDiscount?: boolean;
  isCarousel?: boolean;
  outOfStockText?: string;
  small?: boolean;
  showFinancing?: boolean;
  financingText?: string;
}

const ProductCard = ({
  product,
  _testID,
  index,
  listName,
  newText,
  saleText,
  hideDiscount,
  isCarousel,
  outOfStockText,
  small = false,
}: Props) => {
  const { locale, push } = useCurrentUrl();
  const { vip } = useVipAccess();
  const linkObj = buildLinkObj({ link: product?.deliveryKey });

  const productClick = React.useCallback(
    async (e: any) => {
      e.preventDefault();

      await logProduct('PRODUCT_CLICKED', {
        product: product,
        list_name: listName,
        locale,
        position: index,
      });

      const linkHref = linkObj.href || product.productUrl;
      if (linkHref) push(linkHref);
    },
    [product, index, listName, linkObj, push, locale],
  );

  if (!product) return null;

  const { title, image } = normalize(product);

  return (
    <div
      role="group"
      aria-label="product"
      data-test-id={`product_card_${_testID}${index}`}
      className={classNames(
        isCarousel && 'border border-warm-300',
        'relative h-full z-10 bg-white focus:outline-none',
        // focus
        'focus:outline-none focus:outline-2 focus:ring focus:ring-brand-200',
        // after
        'after:absolute after:-z-20 after:-inset-0 after:transition after:duration-300 after:ease-in-out after:bg-gradient-to-br after:from-white after:via-white after:to-warm-100 after:opacity-0',
        // hover
        'hover:after:opacity-100',
      )}
    >
      <Anchor
        link={linkObj.href}
        onClick={productClick}
        className="absolute top-0 left-0 w-full h-full"
        tabIndex={-1}
      />
      <div
        className={classNames(
          'grid h-full justify-items-center text-center content-end grid-rows-[auto_1fr]',
          small ? 'px-4 pb-4 gap-3 pt-4' : 'md:px-16 pb-8 gap-5',
        )}
        data-testid="pc-ribbon"
      >
        {!small && (
          <ProductBanner
            product={product}
            outOfStockText={outOfStockText}
            newText={newText}
            saleText={saleText}
            renderEmpty
          />
        )}
        <div className={classNames(small && 'gap-1', 'grid grid-rows-1 gap-4 justify-center')}>
          <Anchor
            link={linkObj.href}
            onClick={productClick}
            className="self-center cursor-pointer"
            dataTestId="pc-link"
          >
            {title && (
              <h3
                className={classNames(
                  'relative tra-text-web-heading-6 mb-0',
                  small && 'text-lg leading-none',
                )}
                data-testid="pc-title"
              >
                {title}
              </h3>
            )}
          </Anchor>
          <div className="self-end">
            <ProductPrice
              product={product}
              className="card-price relative"
              align="center"
              variant={small && 'small'}
              hideDiscount={hideDiscount}
              vip={vip}
            />
            <div className="flex justify-center py-4" data-testid="pc-review-container">
              <RatingStars value={product?.ratingSummary?.average} />
            </div>
          </div>
        </div>
        <div className={classNames('relative -z-10', small && 'max-w-[100px]')}>
          {image && (
            <Image
              src={image}
              alt=""
              height={200}
              width={200}
              quality={75}
              data-testid="pc-image"
              loading="eager"
              objectFit="cover"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
